import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import environment from '../../environment/environment';
import { TravelTaked } from 'app/viaje/services/TravelsCordinate';
import { driverShiftBuilder } from 'app/chofer/models/DriverShift';
import { useViajesTraslado } from 'app/viaje/services';
import { ColorState } from 'app/viaje/viajeTraslado/viajeTraslado/components/ViajeTrasladoBaseFilters';
import { fetchTotalStatesReservations } from 'shared/services/shared/trafficLightsService';

const TRAFFIC_LIGHTS_ROUTES = [
  '/main/operations/coordination/pending-trips',
  '/main/operations/coordination/open-trips'
];


export interface ReservationStateChanged {
  id: number;
  color?: string;
  order?: number;
  state?: string;
  show_waiting_clock?: boolean;
  state_datetime?: string;
  total_minutes_waiting: number | null;
}


export interface Biller {
  id: number;
  name: string;
  cuit: number | string;
  mail: string;
  photo: string | null;
  accountNumber: string | null;
  accountOwnerName: string | null;
  alias: string | null;
  bankName: string | null;
  cbu: string | null;
  ingresos_brutos_start_date: string;
  monotributo_start_date: string;
  isDeleted: boolean;
}

export interface BusinessUnit {
  id: number;
  name: string;
  description: string;
}

export interface ShiftAddress {
  street: string;
  streetNumber: number;
  floor: string;
  apartment: string;
  locality: string;
  [key: string]: unknown;
}

export interface VehicleModel {
  id: number;
  name?: string;
  brand?: string;
  [key: string]: unknown;
}

export interface Vehicle {
  id: number;
  patent: string;
  category: string;
  photo: string | null;
  model: VehicleModel;
  [key: string]: unknown;
}

export interface AssignedDriver {
  id: number;
  code: number;
  name: string;
  category: string;
  phone: string;
  photo: string | null;
  hasPosnet: boolean;
  opened_shift: boolean;
  isDeleted: boolean;
  state: string | null;
  billers: Biller[];
  business_units: BusinessUnit;
  shift_address: ShiftAddress;
  vehicle: Vehicle;
}

export interface Client {
  id: number;
  name: string;
  cuit: string;
  type: string;
  hasPriority: boolean;
}

export interface LocationItem {
  id?: number;
  address?: string;
  latitude?: number;
  longitude?: number;
  type?: string;
  order?: number;
  [key: string]: unknown;
}

export interface Applicant {
  id?: number;
  name?: string;
  email?: string;
  phone?: string;
  [key: string]: unknown;
}

export interface AuthorizedPerson {
  id?: number;
  name?: string;
  [key: string]: unknown;
}

export interface ServiceRequirements {
  applicant: Applicant;
  costCenter: string | null;
  subCostCenter: string | null;
  sector: string | null;
  authorized: AuthorizedPerson;
  [key: string]: unknown;
}

export interface WaitingClockVisibleNotification {
  id: number;
  advanceHours: number;
  arrivalDatetime: string;
  arrival_notification_channel: string;
  assigned_driver: AssignedDriver;
  cancel_observation: string | null;
  client: Client;
  clientType: string;
  coordinationObservation: string | null;
  deviation_observation: string | null;
  entryChannel: string;
  event: unknown | null;
  event_id: number | null;
  event_name: string | null;
  hasPriority: boolean;
  has_out_site: boolean;
  is_arrive_ok: boolean | null;
  is_event: boolean | null;
  locations: LocationItem[];
  passenger_digital_signature: string | null;
  pay_with_cash: boolean;
  platformId: number | string | null;
  pre_assigned_driver: AssignedDriver | null;
  requirements: ServiceRequirements;
  reservationDatetime: string;
  serviceType: string;
  setback_comments: string | null;
  show_waiting_clock: boolean;
  state: string;
  state_datetime: string;
  taken_by_coordination: unknown | null;
  total_minutes_waiting: number | null;
}

export interface StatusItem {
  category: string;
  ids: number[];
}

interface RedSection {
  statuses: StatusItem[];
  totals: number;
}

interface ColorSection {
  totals: number;
}

interface DataTrafficLightsObject {
  red: RedSection;
  green: ColorSection;
  yellow: ColorSection;
  orange: ColorSection;
  violet: ColorSection;
  reservation_changed: ReservationStateChanged | null;
}


export interface TotalStatesReservations {
  red: { total: number; statuses: StatusItem[]; animate: boolean };
  orange: { total: number; animate: boolean };
  yellow: { total: number; animate: boolean };
  green: { total: number; animate: boolean };
  violet: { total: number; animate: boolean };
  reservationStateChanged: ReservationStateChanged | null;
}
// Tipos para el contexto
export interface INotificationsCoordinationContext {
  driversRequests: any[];
  travelsRejected: any[];
  driversExceededRequests: any[];
  travelsTaked: TravelTaked[];
  setTravelsTaked: React.Dispatch<React.SetStateAction<TravelTaked[]>>;
  setDriversRequests: React.Dispatch<React.SetStateAction<any[]>>;
  setTravelsRejected: React.Dispatch<React.SetStateAction<any[]>>;
  setDriversExceededRequests: React.Dispatch<React.SetStateAction<any[]>>;
  getTravelsTaked: () => Promise<void>;
  shouldCoordinationReload: boolean;
  shouldOpenedReload: boolean;
  resetCoordinationReloadFlag: () => void;
  resetOpenedReloadFlag: () => void;
  totalStatesReservations: TotalStatesReservations;
  stopAnimationTrafficLights: (color: ColorState) => void;
  refreshTrafficLights: () => Promise<void>;
  isRefreshingTrafficLights: boolean;
  trafficLightsRefreshError: string | null;
}

// Eventos del socket
enum Events {
  DRIVER_CLOSE_SHIFT = 'driver_close_shift',
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  DRIVER_APPROVED_SHIFT = 'driver_approved_shift',
  RESERVATION_REJECTED_BY_DRIVER = 'reservation_rejected_by_driver',
  RESERVATION_TAKES = 'transfer_reservations_take_untake',
  DRIVER_EXTEND_EXCEEDED_SHIFT = 'driver_extend_exceeded_shift',
  TRANSFER_RESERVATION_TO_ASSIGN_RELOAD = 'transfer_reservation_to_assign_reload',
  TRANSFER_RESERVATION_OPENED_RELOAD = 'transfer_reservation_opened_reload',
  TOTAL_STATES_RESERVATIONS = 'total_states_reservations',
  RESERVATION_STATE_CHANGED = 'reservation_state_changed',
  DRIVER_WAITING_CLOCK_VISIBLE = 'waiting_clock_visible_notification'
}

// Creación del contexto
export const NotificationsCoordinationContext =
  createContext<INotificationsCoordinationContext>({
    driversRequests: [],
    driversExceededRequests: [],
    travelsTaked: [],
    setTravelsTaked: () => { },
    setDriversRequests: () => { },
    setDriversExceededRequests: () => { },
    travelsRejected: [],
    setTravelsRejected: () => { },
    getTravelsTaked: async () => { },
    shouldCoordinationReload: false,
    shouldOpenedReload: false,
    resetCoordinationReloadFlag: () => { },
    resetOpenedReloadFlag: () => { },
    totalStatesReservations: {
      red: { total: 0, statuses: [], animate: false },
      orange: { total: 0, animate: false },
      yellow: { total: 0, animate: false },
      green: { total: 0, animate: false },
      violet: { total: 0, animate: false },
      reservationStateChanged: null
    },
    stopAnimationTrafficLights: () => { },
    refreshTrafficLights: async () => { },
    isRefreshingTrafficLights: false,
    trafficLightsRefreshError: null,
  });

const TRAFFIC_LIGHTS_KEY = 'totalStatesReservations';

const loadTrafficLightsState = (): TotalStatesReservations => {
  try {
    const stored = localStorage.getItem(TRAFFIC_LIGHTS_KEY);
    if (stored) {
      const parsed: TotalStatesReservations = JSON.parse(stored);
      return {
        ...parsed,
        red: { ...parsed.red, animate: false },
        orange: { ...parsed.orange, animate: false },
        yellow: { ...parsed.yellow, animate: false },
        green: { ...parsed.green, animate: false },
        violet: { ...parsed.violet, animate: false }
      };
    }
  } catch { }
  return {
    red: { total: 0, statuses: [], animate: false },
    orange: { total: 0, animate: false },
    yellow: { total: 0, animate: false },
    green: { total: 0, animate: false },
    violet: { total: 0, animate: false },
    reservationStateChanged: null
  };
};

const STALE_TIMEOUT_MS = 3 * 60 * 1000;
const FALLBACK_CHECK_INTERVAL_MS = 60 * 1000;

const buildTotalStatesReservations = (
  data: DataTrafficLightsObject,
  prev: TotalStatesReservations,
): TotalStatesReservations => ({
  red: {
    total: data.red?.totals ?? prev.red.total,
    statuses: data.red?.statuses?.map((status: any) => ({
      category: status.category,
      ids: status.ids
    })) ?? prev.red.statuses,
    animate: (data.red?.totals ?? prev.red.total) > prev.red.total
  },
  orange: {
    total: data.orange?.totals ?? prev.orange.total,
    animate: (data.orange?.totals ?? prev.orange.total) > prev.orange.total
  },
  yellow: {
    total: data.yellow?.totals ?? prev.yellow.total,
    animate: (data.yellow?.totals ?? prev.yellow.total) > prev.yellow.total
  },
  violet: {
    total: data.violet?.totals ?? prev.violet.total,
    animate: (data.violet?.totals ?? prev.violet.total) > prev.violet.total
  },
  green: {
    total: data.green?.totals ?? prev.green.total,
    animate: (data.green?.totals ?? prev.green.total) > prev.green.total
  },
  reservationStateChanged: data.reservation_changed
});

export const NotificationsProvider = ({ children }) => {
  const tripRepository = useViajesTraslado();
  const location = useLocation();
  const isOnWatchedRoute = TRAFFIC_LIGHTS_ROUTES.some((route) =>
    location.pathname.startsWith(route)
  );
  const [driversRequests, setDriversRequests] = useState<any[]>([]);
  const [driversExceededRequests, setDriversExceededRequests] = useState<any[]>(
    []
  );
  const [travelsRejected, setTravelsRejected] = useState<any[]>([]);
  const [travelsTaked, setTravelsTaked] = useState<TravelTaked[]>([]);
  const [shouldCoordinationReload, setShouldCoordinationReload] =
    useState<boolean>(true);
  const [shouldOpenedReload, setShouldOpenedReload] = useState<boolean>(false);
  const [totalStatesReservations, setTotalStatesReservations] = useState<TotalStatesReservations>(loadTrafficLightsState);
  const [isRefreshingTrafficLights, setIsRefreshingTrafficLights] = useState(false);
  const [trafficLightsRefreshError, setTrafficLightsRefreshError] = useState<string | null>(null);
  const lastUpdatedRef = useRef<number>(Date.now());

  useEffect(() => {
    localStorage.setItem(TRAFFIC_LIGHTS_KEY, JSON.stringify(totalStatesReservations));
  }, [totalStatesReservations]);

  const stopAnimationTrafficLights = (color: ColorState) => {
    setTotalStatesReservations(prev => {
      if (color === ColorState.RED) return { ...prev, red: { ...prev.red, animate: false } };
      if (color === ColorState.ORANGE) return { ...prev, orange: { ...prev.orange, animate: false } };
      return { ...prev, yellow: { ...prev.yellow, animate: false } };
    });
  };


  useEffect(() => {
    const socket: Socket = io(environment.notifications, {
      autoConnect: true,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      timeout: 10000 // Esperar 10 segundos antes de marcar timeout
    });

    // Funciones para manejar eventos
    const onDriverCloseShift = (data: any) =>
      setDriversRequests((prev) => [...prev, data]);
    const onDriverApprovedShift = (data: any) =>
      setDriversRequests((prev) => prev.filter((req) => req.id !== data.id));
    const onReservationRejectedByDriver = (data: any) =>
      setTravelsRejected((prev) => [...prev, data]);

    const onTakeTravelByUser = (data: any) => {
      setTravelsTaked((prev) =>
        data.action === 'UN_TAKE'
          ? prev.filter(
            (travel) =>
              travel.transfer_reservation_id !== data.transfer_reservation_id
          )
          : [...prev.filter((travel) => travel.user_id !== data.user_id), data]
      );
    };

    const onDriverExtendExceededShift = (data: any) => {
      const driver = driverShiftBuilder.fromBackEnd(data);
      setDriversExceededRequests((prev) => [...prev, driver]);
    };

    const onReservationCoordinationReload = () => {
      setShouldCoordinationReload(true);
    };

    const onOpenedTravelTableReload = () => {
      setShouldOpenedReload(true);
    };

    const onTotalStatesReservations = (data: DataTrafficLightsObject) => {
      try {
        lastUpdatedRef.current = Date.now();
        setTotalStatesReservations((prev) => buildTotalStatesReservations(data, prev));
      } catch (error) {
        console.error('❌ Error obteniendo total states reservations:', error);
      }
    };

    const onWaitingClockVisible = (data: WaitingClockVisibleNotification) => {
      try {
        const reservation = data;
        setTotalStatesReservations((prev) => ({
          ...prev,
          reservationStateChanged: {
            id: reservation?.id,
            state: reservation?.state,
            show_waiting_clock: data?.show_waiting_clock,
            state_datetime: data?.state_datetime,
            total_minutes_waiting: data?.total_minutes_waiting
          }
        }));
      } catch (error) {
        console.error('❌ Error procesando driver.waiting_clock_visible:', error);
      }
    };

    // Suscribirse a eventos
    socket.on(Events.DRIVER_CLOSE_SHIFT, onDriverCloseShift);
    socket.on(Events.DRIVER_APPROVED_SHIFT, onDriverApprovedShift);
    socket.on(Events.DRIVER_EXTEND_EXCEEDED_SHIFT, onDriverExtendExceededShift);
    socket.on(
      Events.RESERVATION_REJECTED_BY_DRIVER,
      onReservationRejectedByDriver
    );
    socket.on(Events.RESERVATION_TAKES, onTakeTravelByUser);
    socket.on(
      Events.TRANSFER_RESERVATION_TO_ASSIGN_RELOAD,
      onReservationCoordinationReload
    );
    socket.on(
      Events.TRANSFER_RESERVATION_OPENED_RELOAD,
      onOpenedTravelTableReload
    );
    socket.on(Events.TOTAL_STATES_RESERVATIONS, onTotalStatesReservations);
    socket.on(Events.DRIVER_WAITING_CLOCK_VISIBLE, onWaitingClockVisible);
    socket.on(Events.CONNECT, () => console.log('✅ Conectado a WebSocket'));
    socket.on(Events.DISCONNECT, () =>
      console.log('🔴 Desconectado de WebSocket')
    );

    // Cleanup cuando el componente se desmonta
    return () => {
      socket.off(Events.DRIVER_CLOSE_SHIFT, onDriverCloseShift);
      socket.off(Events.DRIVER_APPROVED_SHIFT, onDriverApprovedShift);
      socket.off(
        Events.RESERVATION_REJECTED_BY_DRIVER,
        onReservationRejectedByDriver
      );
      socket.off(Events.RESERVATION_TAKES, onTakeTravelByUser);
      socket.off(
        Events.TRANSFER_RESERVATION_TO_ASSIGN_RELOAD,
        onReservationCoordinationReload
      );
      socket.off(
        Events.TRANSFER_RESERVATION_OPENED_RELOAD,
        onOpenedTravelTableReload
      );
      socket.off(Events.TOTAL_STATES_RESERVATIONS, onTotalStatesReservations);
      socket.off(Events.DRIVER_WAITING_CLOCK_VISIBLE, onWaitingClockVisible);
      socket.disconnect();
    };
  }, []); // Solo ejecutarlo una vez

  // Función para obtener viajes tomados
  const getTravelsTaked = async () => {
    try {
      const response = await fetch(
        `${environment.matching}/transfer-reservations-takes`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'X-Api-Key': environment.matchingKeys
          }
        }
      );

      if (!response.ok) throw new Error('Error en la API');

      const result = await response.json();
      setTravelsTaked(result);
    } catch (error) {
      console.error('❌ Error obteniendo travelsTaked:', error);
    }
  };

  useEffect(() => {
    if (!isOnWatchedRoute) return;

    const interval = setInterval(async () => {
      if (Date.now() - lastUpdatedRef.current < STALE_TIMEOUT_MS) return;
      try {
        const data = await fetchTotalStatesReservations();
        lastUpdatedRef.current = Date.now();
        setTotalStatesReservations((prev) => buildTotalStatesReservations(data, prev));
      } catch (error) {
        console.error('❌ Error en fallback semáforo:', error);
      }
    }, FALLBACK_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isOnWatchedRoute]);

  const refreshTrafficLights = async () => {
    setIsRefreshingTrafficLights(true);
    setTrafficLightsRefreshError(null);
    try {
      const data = await fetchTotalStatesReservations();
      lastUpdatedRef.current = Date.now();
      setTotalStatesReservations((prev) => buildTotalStatesReservations(data, prev));
    } catch (error) {
      setTrafficLightsRefreshError('No se pudo actualizar el semáforo');
      console.error('❌ Error refrescando semáforo:', error);
    } finally {
      setIsRefreshingTrafficLights(false);
    }
  };

  const resetCoordinationReloadFlag = () => {
    setShouldCoordinationReload(false);
  };

  const resetOpenedReloadFlag = () => {
    setShouldOpenedReload(false);
  };

  return (
    <NotificationsCoordinationContext.Provider
      value={{
        driversRequests,
        travelsTaked,
        driversExceededRequests,
        setTravelsTaked,
        setDriversRequests,
        travelsRejected,
        setTravelsRejected,
        setDriversExceededRequests,
        getTravelsTaked,
        shouldCoordinationReload,
        shouldOpenedReload,
        resetCoordinationReloadFlag,
        resetOpenedReloadFlag,
        totalStatesReservations,
        stopAnimationTrafficLights,
        refreshTrafficLights,
        isRefreshingTrafficLights,
        trafficLightsRefreshError,
      }}
    >
      {children}
    </NotificationsCoordinationContext.Provider>
  );
};

export const useNotificationsContext = () => {
  return useContext(NotificationsCoordinationContext);
};
